/* eslint-disable no-case-declarations */
import * as types from 'constants/ActionTypes';
import { camelizeKeys } from 'humps';

/**
 * Returns an object for trip details
 * @param {*} data
 * @returns
 */
function getTripObject(data) {
  return {
    originDisplay: data.origin,
    originSlug: data.origin_slug,
    destinationDisplay: data.destination,
    destinationSlug: data.destination_slug,
    date: data.search_date,
    time: data.time,
  };
}

const initialState = {
  isLoading: false,
  ticket: {
    openTicket: '',
    origin: '',
    destination: '',
    operationDate: '',
    email: '',
    firstName: '',
    lastName: '',
    secondLastName: '',
    ticketPrice: '',
    operationNumber: '',
    nit: '',
    document: '',
    departCancelAllowed: false,
    returnCancelAllowed: false,
    isPix: false,
  },
  purchaseToken: '',
  idCancellation: null,
  cancelStatus: null,
  cancelDate: '',
  hasConnections: false,
  hasConnectionsReturns: false,
  parentHasFlatfare: false,
  trip: {
    originDisplay: '',
    originSlug: '',
    destinationDisplay: '',
    destinationSlug: '',
    date: '',
    time: '',
  },
  trips: {
    tripDepart: {},
    tripReturn: {},
    tripDepartConnection: [],
    tripReturnConnection: [],
  },
  allPassengers: {
    depart: [],
    return: [],
  },
  selectedPassengerToAlter: {
    ticketKey: '',
    nodeTripName: '',
  },
  statusRescheduleLoading: false,
  statusRescheduleData: null,
  rescheduleTicketLoading: false,
  rescheduleTicketData: null,
  /** From `/exchange/schedule/:op/...` when validating (e.g. EE8FAA); differs from ticket transporter keys. */
  routeOperationSegment: '',
};

/**
 * Exchange reducer function.
 *
 * @param {object} state - The current exchange state.
 * @param {object} action - The action object containing type and payload.
 * @returns {object} The new exchange state.
 */
export default function exchange(state = initialState, action) {
  switch (action.type) {
    case types.EXCHANGE_TYPEAHEAD_CHANGE:
      return {
        ...state,
        trip: {
          ...state.trip,
          [`${action.name}Display`]: action.display,
          [`${action.name}Slug`]: action.slug,
        },
      };
    case types.EXCHANGE_TYPEAHEAD_CLEAR:
      const origin = action.origin ? { originDisplay: '', originSlug: '' } : {};
      const destination = action.destination ? { destinationDisplay: '', destinationSlug: '' } : {};

      return {
        ...state,
        trip: {
          ...state.trip,
          ...origin,
          ...destination,
        },
      };
    case types.EXCHANGE_DATEPICKER_CHANGE:
      return {
        ...state,
        trip: {
          ...state.trip,
          date: action.date,
        },
      };
    case types.EXCHANGE_SET_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
      };
    case types.CANCEL_TICKET_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
      };

    case types.STATUS_CANCEL_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
      };

    case types.STATUS_CANCEL_RECEIVE:
      return {
        ...state,
        cancelStatus: action.data.status,
        cancelDate: action.data.created_at,
      };

    case types.STATUS_CANCEL_RESET:
      return {
        ...state,
        cancelStatus: null,
        cancelDate: '',
      };
    case types.CANCEL_TICKET_RECEIVE:
      return {
        ...state,
        idCancellation: action.data.id,
      };
    case types.EXCHANGE_RECEIVE:
      const { data } = action;
      const { tickets } = data;

      const hasConnections = data.connections && data.connections.length > 0;
      const hasConnectionsReturns =
        !!data.returns && !!data.returns.connections && data.returns.connections.length > 0;

      const trips = {
        tripDepart: getTripObject(data),
        tripReturn: {},
        tripDepartConnection: hasConnections ? data.connections : [],
        tripReturnConnection: hasConnectionsReturns ? data.returns.connections : [],
      };

      if (!!data.returns && data.returns !== null) {
        trips.tripReturn = getTripObject(data.returns);
      }

      // Check if original ticket was purchased with flatfare
      const parentHasFlatfare = data.flat_fare === true;

      // Check if we have valid tickets in depart or return
      const hasDepartTickets = !hasConnections && tickets && tickets.length > 0;
      const hasReturnTickets =
        !hasConnectionsReturns &&
        data.returns &&
        data.returns.tickets &&
        data.returns.tickets.length > 0;

      // Get identity_document_id from first ticket, fallback to params.document for compatibility
      const firstTicket = !hasConnections
        ? tickets && tickets.length > 0 && tickets[0]
        : data.connections &&
          data.connections.length > 0 &&
          data.connections[0].tickets &&
          data.connections[0].tickets.length > 0 &&
          data.connections[0].tickets[0];

      // If no depart ticket, try to get from return tickets
      const firstReturnTicket = hasReturnTickets && data.returns.tickets[0];

      const identityDocumentId =
        (firstTicket && firstTicket.identity_document_id) ||
        (firstReturnTicket && firstReturnTicket.identity_document_id) ||
        (data.params && data.params.document);

      // Determine ticket price from depart or return tickets
      let ticketPrice = null;
      if (hasDepartTickets) {
        ticketPrice = tickets[0].ticket_price;
      } else if (hasReturnTickets) {
        ticketPrice = data.returns.tickets[0].ticket_price;
      }

      // Determine operation number from connections, depart, or return tickets
      let operationNumber = null;
      if (hasConnections) {
        operationNumber = data.connections[0]?.tickets?.[0]?.transporter_key;
      } else if (hasDepartTickets) {
        operationNumber = tickets[0].transporter_key;
      } else if (hasReturnTickets) {
        operationNumber = data.returns.tickets[0].transporter_key;
      }

      return {
        ...state,
        purchaseToken: !!data.purchase_token && data.purchase_token,
        hasConnections,
        hasConnectionsReturns,
        trips,
        parentHasFlatfare,
        routeOperationSegment:
          typeof action.routeOperationSegment === 'string'
            ? action.routeOperationSegment.trim()
            : state.routeOperationSegment,
        ticket: {
          ...state.ticket,
          openTicket: data.open_ticket,
          origin: data.origin,
          destination: data.destination,
          operationDate: data.operation_date,
          email: (data.params && data.params.email) || data.email,
          ticketPrice,
          operationNumber,
          nit: data.lock_id || action.nit,
          document: identityDocumentId,
          departCancelAllowed: !!data.cancel_allowed,
          returnCancelAllowed: !!data.returns?.cancel_allowed,
          isPix: !!data.pix,
        },
        operationNumbers: hasDepartTickets
          ? camelizeKeys(tickets).map((passenger) => passenger.transporterKey)
          : [],
        passengers: hasDepartTickets ? camelizeKeys(tickets) : [],
        allPassengers: {
          depart: hasDepartTickets ? camelizeKeys(tickets) : [],
          return: hasReturnTickets ? camelizeKeys(data.returns.tickets) : [],
        },
        trip: getTripObject(data),
      };

    case types.EXCHANGE_RESET:
      return initialState;

    case types.ERROR_TRIP_EXCHANGE:
      return {
        ...state,
        errorTrip: action.errorTrip,
      };

    case types.EXCHANGE_UPDATE_PASSENGERS:
      return {
        ...state,
        passengers: action.passengers,
        trip: {
          ...state.trip,
          originDisplay: action.trip.originDisplay,
          originSlug: action.trip.originSlug,
          destinationDisplay: action.trip.destinationDisplay,
          destinationSlug: action.trip.destinationSlug,
          date: action.trip.date,
          time: action.trip.time,
        },
      };

    case types.EXCHANGE_SELECTED_PASSENGER_TO_ALTER:
      return {
        ...state,
        selectedPassengerToAlter: {
          ticketKey: action.selectedPassengerToAlter.ticketKey,
          nodeTripName: action.selectedPassengerToAlter.nodeTripName,
        },
      };

    case types.STATUS_RESCHEDULE_LOADING:
      return {
        ...state,
        statusRescheduleLoading: action.isLoading,
      };

    case types.STATUS_RESCHEDULE_RECEIVE:
      return {
        ...state,
        statusRescheduleData: action.data,
        statusRescheduleLoading: false,
      };

    case types.RESCHEDULE_TICKET_LOADING:
      return {
        ...state,
        rescheduleTicketLoading: action.isLoading,
      };

    case types.RESCHEDULE_TICKET_RECEIVE:
      return {
        ...state,
        rescheduleTicketData: action.data,
        rescheduleTicketLoading: false,
      };

    default:
      return state;
  }
}
